<template>
  <b-modal
    :append-to-body="true"
    :active="true"
    aria-role="dialog"
    aria-modal
    trap-focus
    full-screen
    scroll="keep"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <div>
          {{ $t('mte_management') }}
        </div>
        <div
          class="modal-card-head-right"
          v-if="currentTask && currentTask._id"
        >
          <b-button icon-left="plus" @click="addInstrument">
            Add
            <img class="img-icon" src="/img/diagram_icon.png" />
          </b-button>
        </div>
      </header>

      <section class="modal-card-body">
        <b-dropdown
          aria-role="list"
          :close-on-click="true"
          :scrollable="true"
          :max-height="500"
          class="dropdown-up"
          v-model="selectedTask"
        >
          <template #trigger="{ active }">
            <b-field label="Tasks">
              <b-input
                readonly
                v-model="dropdownInputText"
                :icon-right="active ? 'menu-up' : 'menu-down'"
                @input="onTextEnter"
              ></b-input>
            </b-field>
          </template>
          <b-dropdown-item aria-role="listitem" :value="null">
            All
          </b-dropdown-item>

          <template v-for="task in tasks">
            <b-dropdown-item aria-role="listitem" :value="task">
              {{ task.taskName }}
            </b-dropdown-item>
          </template>
        </b-dropdown>

        <b-table
          class="history-table"
          :data="tableData"
          :narrowed="true"
          :loading="isLoading"
          :sticky-header="true"
          :mobile-cards="false"
          height="100%"
        >
          <template slot-scope="props">
            <b-table-column field="instrumentId" label="Instrument ID" sortable>
              {{ props.row.instrumentId }}
            </b-table-column>

            <b-table-column field="instrumentName" label="Name" sortable>
              {{ props.row.instrumentName }}
            </b-table-column>

            <b-table-column field="typeName" label="Type Name" sortable>
              {{ props.row.typeName }}
            </b-table-column>

            <b-table-column field="taskName" label="Task Name" sortable>
              {{ props.row.taskName }}
            </b-table-column>

            <b-table-column field="usedBy" label="Used By" sortable centered>
              {{ props.row.usedBy }}
            </b-table-column>

            <b-table-column field="usedOn" label="Used On" sortable centered>
              {{ new Date(props.row.usedOn).toLocaleDateString() }}
            </b-table-column>

            <b-table-column
              field="calibrationDueDateAtlogging"
              label="Calibration Date (at the time of logging)"
              sortable
              centered
            >
              {{
                new Date(
                  props.row.calibrationDueDateAtlogging
                ).toLocaleDateString()
              }}
            </b-table-column>
          </template>
          <template #empty>
            <div class="has-text-centered">No records</div>
          </template>
          <template #bottom-left> </template>
        </b-table>
      </section>
      <footer class="modal-card-foot">
        <b-button @click="close">
          {{ $t('close') }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>
<script>
import { GET_INSTRUMENTATION_JOB_HISTORY } from '../../../store/instrumentations/getters/getterTypes';
import { FETCH_INSTRUMENTATION_JOB_HISTORY } from '../../../store/instrumentations/actions/actionTypes';
import { GET_CURRENT_JOB } from '../../../store/jobs/getters/getterTypes';
import { GET_CURRENT_TASK } from '../../../store/tasks/getters/getterTypes';

export default {
  computed: {
    instrumentHistory() {
      return this.$store.getters[GET_INSTRUMENTATION_JOB_HISTORY];
    },
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    tasks() {
      return this.$store.state.jobTasks;
    },
    currentTask() {
      return this.$store.getters[GET_CURRENT_TASK];
    },

    tableData() {
      if (this.selectedTask && this.selectedTask._id) {
        return this.instrumentHistory.filter(
          (record) => record.taskId === this.selectedTask._id
        );
      }
      if (this.instrumentHistory) return this.instrumentHistory;

      return [];
    },
  },

  data() {
    return {
      isLoading: false,
      dropdownInputText: 'All',
      selectedTask: null,
    };
  },

  watch: {
    selectedTask(newTask) {
      if (newTask) {
        this.dropdownInputText = newTask.taskName;
      } else {
        this.dropdownInputText = 'All';
      }
    },
  },

  async mounted() {
    this.refreshData();
  },

  methods: {
    async refreshData() {
      this.isLoading = true;
      try {
        await this.$store.dispatch(FETCH_INSTRUMENTATION_JOB_HISTORY, {
          jobId: this.job._id,
        });

        if (!this.tasks || (this.tasks && this.tasks.length === 0)) {
          await this.$store.dispatch('getJobTasks', { jobId: this.job._id });
        }

        if (this.currentTask._id) {
          this.selectedTask = this.currentTask;
        }
      } catch (error) {
        console.error('Error fetching instrumentations:', error);
      } finally {
        this.isLoading = false;
      }
    },

    onTextEnter() {
      const searchText = this.dropdownInputText.toLowerCase();
      const matchedTask = this.tasks.find((task) =>
        task.taskName.toLowerCase().includes(searchText)
      );
      if (matchedTask) {
        this.selectedTask = matchedTask;
      } else if (searchText === 'all') {
        this.selectedTask = null;
      }
    },
    addInstrument() {
      this.$emit('addInstrument');
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
<style scoped>
.dropdown {
  height: 70px;
  margin-bottom: 30px;
}

.modal-card-head {
  display: flex;
  justify-content: space-between;
}

.modal-card-foot {
  display: flex;
  justify-content: flex-end;
}

.img-icon {
  height: 16px;
  width: 16px;
  margin-bottom: 5px;
}

.modal-card {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.modal-card-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.history-table {
  flex: 1 1 auto;
  min-height: 0;
}

.history-table ::v-deep .table-wrapper {
  height: 100%;
  overflow: auto;
}

@media (max-width: 1024px) {
  .history-table ::v-deep table.table {
    width: 100% !important;
    table-layout: fixed !important;
  }

  .history-table ::v-deep th,
  .history-table ::v-deep td {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
}

.history-table ::v-deep table {
  font-family: Arial !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
  font-size: 16px !important;
}

.modal-card-head {
  font-family: Arial !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
  font-size: 16px !important;
}
</style>
